import { type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { CircleCheck, Play } from 'lucide-react';
import { type TaskView, type WorkflowCategory, pickTargetStatus } from '@apm/contracts';
import { Button } from '../../../components/ui/button';
import { ProgressQuickAction } from '../../../components/ui/progress-quick-action';
import { errorMessage } from '../../../lib/error-message';
import { boardApi, boardKeys } from '../../board/api';
import { sprintHistoryApi, sprintHistoryKeys } from '../../overview/api';
import { canMoveBetween } from '../status-flags';
import { useCompleteTaskGuard } from '../use-complete-task-guard';

/**
 * Ô cập nhật tiến độ trong popup chi tiết task — APM-TASK-h.
 *
 * ## Vì sao là một component chung, không phải hai khối giống nhau
 *
 * Câu hỏi "lúc nào thì cho bấm" có **bốn** nhánh (quyền, trạng thái, checklist,
 * task cha) và mỗi nhánh có một lý do riêng đã ghi thành chữ. Chép khối ấy sang
 * popup thứ hai nghĩa là lần sau sửa một nhánh sẽ sửa đúng một nửa số chỗ — và
 * nửa còn lại mời người dùng bấm vào một thứ server từ chối.
 *
 * Chỗ **ghi** vẫn thuộc về người gọi (`onChange`): mỗi popup mở từ một danh
 * sách khác nhau nên phải làm mới một query khác nhau. Chung phần quyết định,
 * riêng phần lưu.
 *
 * ## Vì sao "đang làm" mới sửa được
 *
 * "Sẵn sàng" luôn 0%, "Xong" luôn 100% — hai con số ấy là **hệ quả** của trạng
 * thái, nên cách đúng để đổi chúng là chuyển cột, không phải kéo thanh trượt.
 * Kéo tiến độ một việc chưa bắt đầu lên 40% cho ra một hàng nói hai chuyện
 * ngược nhau.
 *
 * Task đã có checklist thì `%` tính từ số mục đã tích (spec 9.1) — chỗ này chỉ
 * hiện lại, và `ProgressQuickAction` tự chuyển sang dạng chỉ-đọc.
 *
 * ## ⚠️ Vì sao "Đóng việc" là một nút RIÊNG, không phải mốc 100% của thanh
 *
 * Theo PMBOK/PRINCE2, **báo tiến độ** ("tôi đi được bao xa") và **đóng việc**
 * ("cái này xong rồi") là hai hành động khác nhau, và người có thẩm quyền làm
 * hai việc ấy có thể là hai người. Kéo thanh lên 100% không được tự đóng việc:
 *
 * - Đóng việc phải qua `ChangeTaskStatusCommand` — nơi có cửa **Definition of
 *   Done** và cửa checklist. Một cú kéo thanh trượt đi vòng qua cả hai cửa ấy.
 * - Và 100% có nghĩa riêng của nó: "tôi đã làm xong phần việc của mình, đang
 *   chờ nghiệm thu". Gộp lại thì trạng thái ấy không còn diễn đạt được.
 *
 * Đó là lý do DEMOW-5 hiện màu quá hạn dù `progress_pct = 100`: nó **chưa** ở
 * cột Xong. Trước đây muốn đóng nó phải sang board kéo thẻ, nên nút này lấp
 * đúng khoảng trống ấy — vẫn đi qua đúng cửa, chỉ là bấm được từ đây.
 *
 * Nút hỏi `task:update`, cùng quyền mà `PATCH /tasks/:id/status` đòi. Ứng dụng
 * cố ý **không** đòi phải là PM: ở phần lớn nhóm, người làm tự kéo thẻ sang
 * Xong còn việc nghiệm thu nằm ở cổng pha (`phase_gate`) và ở DoD — hai cơ chế
 * đã có. Muốn chặt hơn thì đặt DoD cho dự án, đừng khoá nút.
 */
export function TaskProgressSlot({
  task,
  canEdit: canEditProject,
  onChange,
}: {
  readonly task: TaskView;
  /**
   * Người đang xem có `task:update` trên **dự án của task này** hay không. Ô tự
   * gộp thêm `task.canEdit` — quyền trên đúng việc này (APM-TASK-own).
   */
  readonly canEdit: boolean;
  /**
   * `actualStartAt` khi người dùng vừa khai lại giờ bắt đầu thực tế để qua cửa
   * thời gian tối thiểu (APM-TMIN-b) — chỗ gọi gửi kèm tiến độ.
   */
  readonly onChange: (progressPct: number, actualStartAt?: string) => void;
}): ReactNode {
  const { t } = useTranslation();
  const canEdit = canEditProject && (task.canEdit ?? true);
  // 100% là xong: thời gian tối thiểu (APM-TMIN-b) rồi đồng hồ bấm giờ (APM-TIME-done).
  const complete = useCompleteTaskGuard();

  /*
    Không có quyền thì hiện con số nhưng không mời sửa. Dùng `notStarted` để
    `ProgressQuickAction` vẽ thanh chỉ-đọc là sai câu giải thích ("hãy chuyển
    sang Đang làm" — thứ họ cũng không làm được), nên nói thẳng lý do.
  */
  if (!canEdit) {
    return (
      <div className="flex flex-col gap-2">
        <p className="text-xs text-muted-foreground">
          {t('board.progressReadOnly', { value: task.progressPct })}
        </p>
        {/* Không sửa được việc vẫn có thể được đổi trạng thái nó — rule dự án, APM-TSP-d. */}
        {task.canMove === true ? (
          <div className="flex flex-wrap gap-2">
            <MoveButton task={task} target="in_progress" />
            <MoveButton task={task} target="done" />
          </div>
        ) : null}
      </div>
    );
  }

  /*
    Nút đóng việc nằm **ngoài** nhánh "đang làm": một việc còn ở Sẵn sàng vẫn
    đóng được (việc hoá ra không cần làm nữa, hoặc ai đó đã làm xong ở chỗ
    khác), và bắt họ kéo thẻ qua Đang làm chỉ để rồi kéo tiếp sang Xong là bắt
    ghi một dòng lịch sử không đúng sự thật.
  */
  return (
    <div className="flex flex-col gap-2">
      {task.statusCategory === 'in_progress' || task.checklist ? (
        <ProgressQuickAction
          value={task.progressPct}
          checklist={task.checklist}
          // Backlog Agile: tiến độ chỉ báo được khi việc đã vào sprint.
          inBacklog={task.inBacklog === true}
          // Task cha lấy % từ subtask, không nhập tay được (spec 9.1) — server
          // trả 400 nếu cố, nên đừng mời bấm vào một thứ chắc chắn hỏng.
          disabled={task.subtaskCount > 0}
          // 100% là xong — đang bấm giờ việc này thì hỏi trước (APM-TIME-done).
          onChange={(value) =>
            complete.guard(task, value >= 100, (actualStartAt) => onChange(value, actualStartAt))
          }
        />
      ) : (
        <p className="text-xs text-muted-foreground">
          {t('myTasks.progressOnlyInProgress', { value: task.progressPct })}
        </p>
      )}
      {/*
        Hai nút cùng hàng, và **loại trừ nhau** — APM-TASK-j2.

        Việc chưa bắt đầu chỉ có "Bắt đầu làm"; việc đang làm chỉ có "Đóng
        việc". Không bao giờ thấy cả hai, nên không có lúc nào phải chọn giữa
        hai nút — và không có đường nào nhảy thẳng từ "cần làm" sang "xong" mà
        bỏ trống mốc bắt đầu. Xem `MOVE.done.showsFor`.
      */}
      <div className="flex flex-wrap gap-2">
        <MoveButton task={task} target="in_progress" />
        <MoveButton task={task} target="done" />
      </div>
      {complete.dialog}
    </div>
  );
}

/**
 * Nhãn, icon và điều kiện hiện của hai nút chuyển cột.
 *
 * Một bảng tra chứ không hai component: phần khó của cả hai là **giống hệt
 * nhau** (tìm cột đích của dự án, gọi đúng lệnh, dọn đúng cache), và chép nó
 * ra hai bản nghĩa là lần sau sửa danh sách cache sẽ sửa đúng một nửa.
 */
const MOVE = {
  in_progress: {
    icon: Play,
    // Xanh dương = hành động bắt đầu; xanh lá dành riêng cho "xong". Hai nút
    // đứng cạnh nhau nên icon cùng màu sẽ đọc thành hai nút cùng nghĩa.
    iconClass: 'text-primary',
    buttonClass: '',
    titleKey: null,
    labelKey: 'board.startTask',
    toastKey: 'board.taskStarted',
    /** Chỉ mời bắt đầu việc **chưa bắt đầu**. */
    showsFor: (category: WorkflowCategory): boolean => category === 'todo',
  },
  done: {
    icon: CircleCheck,
    iconClass: 'text-rag-green',
    // Nền xanh lá nhạt — người dùng yêu cầu 2026-11-02: nút kết thúc việc phải
    // đọc ra là "xong" ngay từ màu, không phải một viên kính trắng như mọi nút.
    // Lớp utility thắng `.btn-glass` (layer components) nên vành kính vẫn giữ.
    buttonClass: 'bg-rag-green/15 hover:bg-rag-green/25',
    // Nhãn chỉ "Hoàn thành"; cột đích (thường cũng tên "Hoàn thành") vào tooltip.
    titleKey: 'board.closeTaskTitle',
    labelKey: 'board.closeTask',
    toastKey: 'board.taskClosed',
    /**
     * Chỉ việc **đang làm** mới đóng được — APM-TASK-j2.
     *
     * ⚠️ Đây là một quyết định sản phẩm **thay thế** cái cũ, không phải sửa
     * lỗi. Bản trước cho đóng cả việc còn ở "cần làm", với lý lẽ: một việc hoá
     * ra không cần làm nữa vẫn nên đóng thẳng được, và bắt kéo qua "đang làm"
     * chỉ để rồi kéo tiếp sang "xong" là ghi một dòng lịch sử sai sự thật.
     *
     * Lý lẽ ấy vẫn đúng, nhưng nó thua một điều khác: một việc nhảy thẳng từ
     * "cần làm" sang "xong" **không có mốc bắt đầu thật**, nên nó lọt khỏi mọi
     * phép đo lead time và mọi phép so ước-với-thật mà APM-TASK-j vừa dựng.
     * Một hàng như thế trong báo cáo không sai, nó chỉ *trống* — và một ô
     * trống thì không ai truy ra vì sao.
     *
     * Hệ quả phải chấp nhận: việc không cần làm nữa thì **xoá**, hoặc bấm bắt
     * đầu rồi đóng. Cách thứ nhất đúng hơn về ý nghĩa.
     */
    showsFor: (category: WorkflowCategory): boolean => category === 'in_progress',
  },
} as const satisfies Partial<Record<WorkflowCategory, unknown>>;

/**
 * Đưa task sang cột "Đang làm" hoặc "Xong" — xem docblock ở trên về việc vì sao
 * "Đóng việc" tách khỏi thanh tiến độ.
 *
 * ## Vì sao có nút "Bắt đầu làm" — APM-TASK-j
 *
 * Mốc bắt đầu **thật** của một việc được ghi khi nó lần đầu bước vào cột nhóm
 * `in_progress`. Trước đây đường duy nhất để làm điều đó từ popup là... không
 * có: phải sang board kéo thẻ. Nên mốc ấy hoặc không bao giờ được ghi, hoặc
 * được ghi muộn hơn nhiều so với lúc người ta thật sự bắt tay vào.
 *
 * ## Vì sao đọc board để biết cột nào là cột đích
 *
 * Cột "Đang làm" và "Xong" của mỗi dự án là những hàng `workflow_status` riêng
 * với `category` tương ứng, và tên do dự án tự đặt ("Đã nghiệm thu", "In
 * review"…). Không có hằng số nào để tra — phải hỏi dự án.
 *
 * Board là câu truy vấn **đã có sẵn trong cache**: mọi màn hình mở popup này
 * (board, Gantt, Việc của tôi) đều nằm trong một dự án đang mở. Nên gần như
 * mọi lần đây là zero request.
 *
 * `all=true` để lấy đủ cột kể cả khi dự án Agile đang lọc theo sprint — cột
 * đích vẫn là cột ấy bất kể board đang hiện sprint nào.
 *
 * ⚠️ Dự án có **nhiều** cột cùng nhóm ("Đang làm", "Chờ review") thì lấy cột
 * đầu theo `position`: nó là cột trái nhất của nhóm, tức chặng đầu tiên — đúng
 * chỗ một việc vừa bắt đầu phải vào.
 */
function MoveButton({
  task,
  target,
}: {
  readonly task: TaskView;
  readonly target: keyof typeof MOVE;
}): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const spec = MOVE[target];
  // Đóng việc: thời gian tối thiểu (APM-TMIN-b) rồi đồng hồ bấm giờ (APM-TIME-done).
  const complete = useCompleteTaskGuard();

  const board = useQuery({
    queryKey: [...boardKeys.board(task.projectId), 'all'],
    queryFn: () => boardApi.board(task.projectId, undefined, true),
    staleTime: 5 * 60 * 1000,
  });

  /*
    "Bắt đầu làm" ở dự án Agile = làm trong **sprint đang chạy** (người dùng chốt
    2026-10-30, cùng luật `backlogStartTarget` ở server): việc ở backlog hoặc ở sprint
    chưa chạy được đưa sang sprint đang chạy; không có sprint nào đang chạy thì nút mờ
    kèm lý do thay vì bấm rồi mới nhận lỗi. Việc trong sprint = dự án Agile.
  */
  const agile = task.inBacklog === true || (task.sprintId ?? null) !== null;
  const sprints = useQuery({
    queryKey: sprintHistoryKeys.list(task.projectId),
    queryFn: () => sprintHistoryApi.list(task.projectId),
    enabled: target === 'in_progress' && agile,
    staleTime: 60 * 1000,
  });
  const running = sprints.data?.find((sprint) => sprint.status === 'active') ?? null;
  const own = sprints.data?.find((sprint) => sprint.id === task.sprintId) ?? null;
  // Sprint đã đóng: để server trả đúng lỗi khoá sprint, không tự đổi sprint ở đây.
  const leavesForRunning =
    target === 'in_progress' && agile && own?.status !== 'active' && own?.status !== 'completed';
  const blockedNoSprint = leavesForRunning && sprints.isSuccess && running === null;

  /*
    Cột đích phải nhận được việc này: cột chung hoặc cột của **đúng sprint** chứa nó
    (`pickTargetStatus`, cùng luật với server). Lấy cột đầu tiên của cả bảng thì ở dự
    án Agile nhiều sprint nút trúng cột Xong của sprint khác ⇒ 400 "Cột này thuộc
    sprint khác" (phát hiện ở APM-PCOM).
  */
  const column = pickTargetStatus(
    (board.data?.columns ?? []).map((entry) => entry.status),
    leavesForRunning && running ? running.id : (task.sprintId ?? null),
    target,
  );

  const move = useMutation({
    mutationFn: ({ statusId, actualStartAt }: { statusId: string; actualStartAt?: string }) =>
      /*
        `position: 0` — lên đầu cột đích. Cột Xong xếp theo thứ tự đóng gần
        nhất ở hầu hết nhóm, nên việc vừa chuyển nằm trên cùng là chỗ người ta
        tìm nó.
      */
      boardApi.changeStatus(task.id, statusId, 0, actualStartAt, leavesForRunning),
    onSuccess: () => toast.success(t(spec.toastKey)),
    /*
      Cửa DoD và cửa checklist nằm ở server và trả mã lỗi có cấu trúc
      (`CHECKLIST_INCOMPLETE`…). Hiện nguyên câu ấy chứ không tự đoán trước ở
      client: đoán trước nghĩa là viết lại luật lần thứ hai, và hai bản sẽ lệch.
    */
    onError: (error, variables) => {
      // Chưa đủ thời gian tối thiểu (APM-TMIN-b) ⇒ hộp khai giờ, khai xong thì gửi lại.
      if (
        complete.handleError(
          error,
          task,
          (actualStartAt) => move.mutate({ ...variables, actualStartAt }),
          () => move.mutate(variables),
        )
      ) {
        return;
      }
      toast.error(errorMessage(error, t));
    },
    onSettled: () => {
      /*
        ⚠️ **Chính task này phải nằm trong danh sách** — APM-TASK-j2.

        Popup chi tiết đọc `['task', id]` của riêng nó, không đọc từ board. Bỏ
        sót khoá ấy thì cú bấm chạy đúng ở server — trạng thái đổi thật, mốc bắt
        đầu được ghi thật — mà popup đang mở vẫn hiện dữ liệu cũ, nút vẫn nằm
        đó, và người dùng bấm lần nữa tưởng lần đầu hỏng. Đúng cảnh đã gặp khi
        kiểm tay nút "Bắt đầu làm".
      */
      void queryClient.invalidateQueries({ queryKey: ['task', task.id] });
      void queryClient.invalidateQueries({ queryKey: ['board', task.projectId], exact: false });
      void queryClient.invalidateQueries({ queryKey: ['gantt'], exact: false });
      void queryClient.invalidateQueries({ queryKey: ['me', 'tasks'], exact: false });
    },
  });

  // Chưa biết cột nào là cột đích thì không dựng nút: một nút bấm vào không
  // làm gì tệ hơn là không có nút. Rule dự án không cho thì cũng vậy (APM-TSP-d).
  if (!spec.showsFor(task.statusCategory) || !column) return null;
  if (!canMoveBetween(task, task.statusCategory, target)) return null;
  // Chưa biết dự án có sprint đang chạy không thì chưa dựng nút — tránh nút bấm được rồi mờ đi.
  if (leavesForRunning && !sprints.isSuccess && !sprints.isError) return null;

  const Icon = spec.icon;

  if (blockedNoSprint) {
    return (
      <div className="flex basis-full flex-col gap-1">
        <Button type="button" size="sm" variant="outline" disabled className="self-start">
          <Icon aria-hidden className={`size-4 ${spec.iconClass}`} />
          {t(spec.labelKey, { status: column.name })}
        </Button>
        <p className="text-xs text-rag-amber" data-testid="start-needs-running-sprint">
          {t('taskStart.TASK_START_NO_ACTIVE_SPRINT')}
        </p>
      </div>
    );
  }

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className={spec.buttonClass}
        title={spec.titleKey ? t(spec.titleKey, { status: column.name }) : undefined}
        disabled={move.isPending}
        onClick={() =>
          // `mutateAsync`: hộp chọn commit (APM-PCOM-c) chỉ gửi bản báo khi chuyển cột thành công.
          complete.guard(task, target === 'done', (actualStartAt) =>
            move.mutateAsync({
              statusId: column.id,
              ...(actualStartAt ? { actualStartAt } : {}),
            }),
          )
        }
      >
        <Icon aria-hidden className={`size-4 ${spec.iconClass}`} />
        {t(spec.labelKey, { status: column.name })}
      </Button>
      {complete.dialog}
    </>
  );
}
